import { ref } from 'vue'
import { defineStore } from 'pinia'
import { TicketBoard } from './types'
import { objectify } from '@ezio/utils'
import { useEzioAPI } from '@ezio/services'

export const useTicketBoardsStore = defineStore('ticketBoards', () => {
    const { api } = useEzioAPI()

    const all = ref<{ [id: string]: TicketBoard }>({})
    const indexByName = ref<TicketBoard[]>([])

    const fetchCollection = async () => {
        try {
            const response = await api.get('ticket_boards', {
                pagination: false,
                include: 'board_statuses'
            })
            storeIndex(response.data)
        } catch (error) {
            //
        }
    }

    const fetch = async (id: string) => {
        const response = await api.get('ticket_boards/' + id, {
            include: 'board_statuses,ticket_board_members'
        })
        store([response.data])
        return response.data
    }

    const create = async (data: { name: string; description?: string }) => {
        const response = await api.post('ticket_boards', data)
        store([response.data])
        updateIndex(response.data)
        return response.data
    }

    const update = async (id: string, data: any) => {
        const response = await api.put('ticket_boards/' + id, {
            ...data,
            include: 'board_statuses,ticket_board_members'
        })
        store([response.data])
        updateIndex(response.data)
        return response.data
    }

    const remove = async (id: string) => {
        await api.delete('ticket_boards/' + id)
        delete all.value[id]
        indexByName.value = indexByName.value.filter((b) => b.id !== id)
    }

    // Board Statuses
    const createStatus = async (boardId: string, data: { name: string; color?: string; position?: number }) => {
        const response = await api.post('ticket_boards/' + boardId + '/board_statuses', data)
        await fetch(boardId)
        return response.data
    }

    const updateStatus = async (boardId: string, statusId: string, data: any) => {
        const response = await api.put('ticket_boards/' + boardId + '/board_statuses/' + statusId, data)
        return response.data
    }

    const removeStatus = async (boardId: string, statusId: string) => {
        await api.delete('ticket_boards/' + boardId + '/board_statuses/' + statusId)
        await fetch(boardId)
    }

    // Board Members
    const addMember = async (boardId: string, data: { user_id: string; can_manage_board?: boolean; can_manage_members?: boolean; can_manage_tickets?: boolean }) => {
        const response = await api.post('ticket_boards/' + boardId + '/ticket_board_members', data)
        await fetch(boardId)
        return response.data
    }

    const updateMember = async (boardId: string, memberId: string, data: { can_manage_board?: boolean; can_manage_members?: boolean; can_manage_tickets?: boolean }) => {
        await api.put('ticket_boards/' + boardId + '/ticket_board_members/' + memberId, data)
        await fetch(boardId)
    }

    const removeMember = async (boardId: string, memberId: string) => {
        await api.delete('ticket_boards/' + boardId + '/ticket_board_members/' + memberId)
        await fetch(boardId)
    }

    const sortByName = (A: TicketBoard, B: TicketBoard) => {
        const nameA = A.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        const nameB = B.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
    }

    const updateIndex = (board: TicketBoard) => {
        const temp = indexByName.value.slice()
        const idx = temp.findIndex((b) => board.id === b.id)
        if (idx > -1) {
            temp[idx] = board
        } else {
            temp.push(board)
        }
        temp.sort(sortByName)
        indexByName.value = temp
    }

    const storeIndex = (boards: TicketBoard[]) => {
        indexByName.value = boards
        indexByName.value.sort(sortByName)
    }

    const store = (boards: TicketBoard[]) => {
        const items = boards.map((b) => Object.assign({}, all.value[b.id], b))
        all.value = Object.assign({}, all.value, objectify(items))
    }

    const getTicketOrder = (boardId: string): Record<string, string[]> => {
        return all.value[boardId]?.details?.ticketOrder || {}
    }

    const reorderTickets = async (boardId: string, statusKey: string, ticketIds: string[]) => {
        const board = all.value[boardId]
        if (!board) return
        const ticketOrder = { ...(board.details?.ticketOrder || {}), [statusKey]: ticketIds }
        // Optimistic update
        all.value[boardId] = { ...board, details: { ...board.details, ticketOrder } }
        await update(boardId, { details: { ...board.details, ticketOrder } })
    }

    const ensureTicketOrder = async (boardId: string, groupedTickets: Record<string, { id: string; position?: number; createdAt: string }[]>) => {
        const board = all.value[boardId]
        if (!board) return
        const existing = board.details?.ticketOrder || {}
        const ticketOrder: Record<string, string[]> = {}
        let changed = false

        for (const key in groupedTickets) {
            const tickets = groupedTickets[key]
            const currentOrder = existing[key] || []
            const currentSet = new Set(currentOrder)
            const ticketIds = new Set(tickets.map((t) => String(t.id)))

            // Start with existing order, remove deleted tickets
            const cleaned = currentOrder.filter((id: string) => ticketIds.has(id))
            // Add missing tickets at the top, sorted by position then createdAt
            const missing = tickets
                .filter((t) => !currentSet.has(String(t.id)))
                .sort((a, b) => {
                    const posA = a.position ?? 0
                    const posB = b.position ?? 0
                    if (posA !== posB) return posA - posB
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                })
                .map((t) => String(t.id))

            ticketOrder[key] = [...missing, ...cleaned]

            if (ticketOrder[key].length !== currentOrder.length || missing.length > 0 || cleaned.length !== currentOrder.length) {
                changed = true
            }
        }

        // Remove keys for columns that no longer have tickets
        for (const key in existing) {
            if (!(key in groupedTickets)) {
                changed = true
            }
        }

        if (changed) {
            all.value[boardId] = { ...board, details: { ...board.details, ticketOrder } }
            await update(boardId, { details: { ...board.details, ticketOrder } })
        }
    }

    const reset = () => {
        all.value = {}
        indexByName.value = []
    }

    return {
        all,
        byName: indexByName,
        fetchCollection,
        fetch,
        create,
        update,
        remove,
        createStatus,
        updateStatus,
        removeStatus,
        addMember,
        updateMember,
        removeMember,
        getTicketOrder,
        reorderTickets,
        ensureTicketOrder,
        reset,
        store
    }
})
